import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-scroll';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 999;
  padding: 0 56px;
  transition: background 0.3s ease, border-color 0.3s ease;
  border-bottom: 1px solid ${({ $scrolled, theme }) => $scrolled ? `rgba(${theme.colors.textRgb},0.10)` : 'transparent'};
  background: ${({ $scrolled, theme }) => $scrolled ? `rgba(${theme.colors.bgRgb},0.92)` : 'transparent'};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(12px)' : 'none'};

  @media (max-width: 768px) {
    padding: 0 20px;
  }
`;

const HeaderWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  height: 64px;
  display: flex;
  align-items: center;
  gap: 0;

  @media (max-width: 768px) {
    height: 56px;
  }
`;

const LogoMark = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  cursor: pointer;
`;

const LogoImg = styled.img`
  height: 36px;
  width: auto;
  display: block;

  @media (max-width: 768px) {
    height: 30px;
  }
`;

const Wordmark = styled.span`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 17px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: nowrap;

  .amp {
    font-family: 'DM Mono', monospace;
    font-weight: 500;
    padding: 0 1px;
  }
`;

const Nav = styled.nav`
  display: flex;
  gap: 24px;
  margin-left: 20px;
  font-size: 14px;
  color: ${({ theme }) => `rgba(${theme.colors.textRgb},0.85)`};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  cursor: pointer;
  color: ${({ theme }) => `rgba(${theme.colors.textRgb},0.85)`};
  transition: color 120ms ease;
  font-weight: 400;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Spacer = styled.div`
  flex: 1;
`;

const CTAButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background: transparent;
  color: #FF5B3A;
  border: 0;
  padding: 10px 0;
  margin-right: -32px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  flex-shrink: 0;
  transition: color 120ms ease;

  &:hover {
    color: #FF7A5C;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const HamburgerBtn = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const HLine = styled.span`
  display: block;
  width: 20px;
  height: 1.5px;
  background: ${({ theme }) => theme.colors.text.primary};
  border-radius: 1px;
  transition: transform 220ms ease, opacity 220ms ease;
  transform-origin: center;
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 56px;
  left: 0;
  right: 0;
  background: ${({ theme }) => `rgba(${theme.colors.bgRgb},0.98)`};
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.10)`};
  padding: 8px 20px 28px;
  z-index: 998;
  display: flex;
  flex-direction: column;
`;

const MobileNavLink = styled(Link)`
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme }) => `rgba(${theme.colors.textRgb},0.8)`};
  padding: 18px 0;
  border-bottom: 1px solid ${({ theme }) => `rgba(${theme.colors.textRgb},0.07)`};
  cursor: pointer;
  transition: color 120ms ease;
  letter-spacing: -0.01em;

  &:active {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const MobileCTA = styled.a`
  display: block;
  margin-top: 20px;
  padding: 15px;
  background: #FF5B3A;
  color: #ECE8E0;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  border-radius: 8px;
  text-decoration: none;
  transition: background 120ms ease;

  &:active {
    background: #E8431F;
  }
`;

const NAV_LINKS = [
  { label: 'Work', to: 'about' },
  { label: 'Services', to: 'services' },
  { label: 'Team', to: 'team' },
  { label: 'Contact', to: 'contact' },
];


const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <HeaderContainer
        $scrolled={scrolled}
      >
        <HeaderWrapper>
          <LogoMark>
            <LogoImg src="/cnd-logo.png" alt="C&D Software logo" />
            <Wordmark>C<span className="amp">&amp;</span>D Software</Wordmark>
          </LogoMark>

          <Nav>
            {NAV_LINKS.map(({ label, to }) => (
              <NavLink key={to} to={to} smooth duration={500} offset={-64}>{label}</NavLink>
            ))}
          </Nav>

          <Spacer />

          <CTAButton
            href="https://calendly.com/d/dtbv-3sr-wjm/meet-with-cnd"
            target="_blank"
            rel="noopener noreferrer"
            whileTap={{ scale: 0.97 }}
          >
            Start a project →
          </CTAButton>

          <HamburgerBtn
            onClick={() => setMenuOpen(o => !o)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          >
            <HLine style={{
              transform: menuOpen ? 'translateY(6.5px) rotate(45deg)' : 'none',
            }} />
            <HLine style={{ opacity: menuOpen ? 0 : 1 }} />
            <HLine style={{
              transform: menuOpen ? 'translateY(-6.5px) rotate(-45deg)' : 'none',
            }} />
          </HamburgerBtn>
        </HeaderWrapper>
      </HeaderContainer>

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            key="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
          >
            {NAV_LINKS.map(({ label, to }) => (
              <MobileNavLink
                key={to}
                to={to}
                smooth
                duration={500}
                offset={-56}
                onClick={closeMenu}
              >
                {label}
              </MobileNavLink>
            ))}
            <MobileCTA
              href="https://calendly.com/d/dtbv-3sr-wjm/meet-with-cnd"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
            >
              Start a project →
            </MobileCTA>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
